import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SCREENING_CONFIG_API_SERVICE_TOKEN } from '@nk/api-lib';
import { REPORT_ENTITIES_LIMIT } from '@nk/report-block';
import {
  CardComponent,
  CardContentComponent,
  ErrorAlertComponent,
  HighlightTextPipe,
  NormalCasePipe,
  PaginationComponent,
  ProgressComponent,
} from '@nk/ui-lib';
import { DateFormats, LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  EMPTY_SCREENING_ADVERSE_MEDIA_CARD_LIST_DATA,
  ScreeningAdverseMediaCardListData,
  transformToScreeningAdverseMediaCardListItems,
} from '../../utils/screening-adverse-media.utils';

@Component({
  selector: 'nk-screening-adverse-media-card-list',
  imports: [
    AsyncPipe,
    DatePipe,
    DecimalPipe,
    NormalCasePipe,
    TranslateModule,
    ProgressComponent,
    ErrorAlertComponent,
    CardComponent,
    CardContentComponent,
    PaginationComponent,
    HighlightTextPipe,
  ],
  templateUrl: './screening-adverse-media-card-list.component.html',
  styleUrls: ['./screening-adverse-media-card-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningAdverseMediaCardListComponent implements OnInit {
  public screeningEntityUuid = input<string | undefined>();
  public printVersion = input(false);
  public data$!: Observable<ScreeningAdverseMediaCardListData>;
  public dateFormats = DateFormats;
  public printLimit = REPORT_ENTITIES_LIMIT;
  public isLoading = signal(false);
  public error = signal<HttpErrorResponse | null>(null);
  private _filters$ = new BehaviorSubject<{
    page: number;
    limit: number;
  }>({ page: 0, limit: 10 });
  private _screeningConfigService = inject(SCREENING_CONFIG_API_SERVICE_TOKEN);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });

  public ngOnInit(): void {
    const screeningEntityUuid = this.screeningEntityUuid();
    if (!screeningEntityUuid) {
      this.data$ = of(EMPTY_SCREENING_ADVERSE_MEDIA_CARD_LIST_DATA);
      return;
    }

    if (this.printVersion()) {
      this._filters$.next({ page: 0, limit: REPORT_ENTITIES_LIMIT });
    }

    this.isLoading.set(true);
    this.data$ = this._filters$.pipe(
      tap(() => {
        this._reportLoadingStateService?.setLoadingState(this.constructor.name, true);
      }),
      switchMap((filters) => {
        this.error.set(null);
        const offset = filters.page * filters.limit;
        return this._screeningConfigService.screeningAdverseMediaCollectionCache
          .get(screeningEntityUuid, filters.limit, offset)
          .pipe(
            map((collection) => {
              const entities = transformToScreeningAdverseMediaCardListItems(collection?.elements || []);
              return {
                entities,
                pagination: {
                  limit: filters.limit,
                  page: filters.page,
                  totalCount: collection.count,
                },
              };
            }),
            tap(() => {
              this._reportLoadingStateService?.setLoadingState(this.constructor.name, false);
              this.isLoading.set(false);
            }),
            catchError((error) => {
              console.error('Error fetching screening adverse media:', error);
              this.error.set(error);
              this._reportLoadingStateService?.setLoadingState(this.constructor.name, false);
              this.isLoading.set(false);
              return of(EMPTY_SCREENING_ADVERSE_MEDIA_CARD_LIST_DATA);
            }),
          );
      }),
    );
  }

  public onChangePage(page: number): void {
    const value = this._filters$.getValue();
    this._filters$.next({ ...value, page });
  }
}
